import type { FetchError } from 'ofetch'
import type { AsyncData } from 'nuxt/app'
import stripTrackingParams from '../helpers/strip-tracking-params'

interface FilterOption {
    value: String
    title: String
    count?: Number
    hex?: String
}

interface Filters {
  [index: string]: Number | string | boolean | Array<FilterOption>
  available: boolean
  prices: Array<FilterOption>
  sizes: Array<FilterOption>
  colors: Array<FilterOption>
  groups: Array<FilterOption>
  sort: string
  totalProducts: Number
}

interface FiltersResponse {
  filterData: Filters
}

export const useFilterStore = defineStore('filter', () => {
  const { $api } = useNuxtApp()
  const loading = ref<boolean>(false)
  const totalProducts = ref<Number | null>(null)
  const available = ref<Omit<Filters, 'totalProducts'>>({
    available: false,
    prices: [],
    sizes: [],
    colors: [],
    groups: [],
    sort: 'fresh'
  })
  const active = ref<Omit<Filters, 'totalProducts'>>({
    available: false,
    prices: [],
    sizes: [],
    colors: [],
    groups: [],
    sort: 'fresh'
  })
  const forceUpdate = ref<Number | null>(null)

  const load = async ({ params } : {params: any}) => {
    if (loading.value) { return } // Prevents calling twice
    loading.value = true
    const { data } = await useAsyncData(`filters-${JSON.stringify(params)}`, () => $api('filters', { params }) as Promise<FiltersResponse>)

    if (data.value) {
      const filterData = data.value.filterData
      totalProducts.value = filterData.totalProducts
      available.value = filterData
    }

    loading.value = false
    return data
  }

  const update = async ({ params }: {params:any}) => {
    if (loading.value) { return } // Prevents calling twice
    loading.value = true
    const data = await $api('filters', { params }) as FiltersResponse
    if (data) {
      totalProducts.value = data.filterData.totalProducts
      const filterKeys = Object.keys(available.value)
      filterKeys.forEach(key => {
        const updateData = data.filterData[key]
        if (!Array.isArray(updateData)) { return false }
        if (!available.value[key]?.length) { return false }
        available.value[key]?.forEach(availableRow => {
          const updateRow = updateData.find(row => row.value === availableRow.value)
          if (updateRow) {
            availableRow.count = updateRow.count
          } else {
            availableRow.count = 0

            if (Array.isArray(active.value[key]) && active.value[key]?.includes(availableRow.value)) {
              active.value[key].splice(active.value[key].indexOf(availableRow.value), 1)
            }
          }
        })
      })
    }

    loading.value = false
  }

  const toggle = ({ filter, value } : {filter: string, value: any}) => {
    forceUpdate.value = Date.now()
    if (!Array.isArray(active.value[filter])) {
      active.value[filter] = value
      return
    }

    if (active.value[filter].includes(value)) {
      return active.value[filter].splice(active.value[filter].indexOf(value), 1)
    }
    return active.value[filter].push(value)
  }

  const setFilter = ({ filter, value } : {filter:string, value: any}) => {
    if (!Array.isArray(active.value[filter])) {
      return (active.value[filter] = value)
    }

    if (!active.value[filter].includes(value)) {
      return active.value[filter].push(value)
    }
  }

  const setFromQuery = (payload: Object) => {
    const filtersToSet = Object.assign({}, stripTrackingParams(payload))

    Object.keys(filtersToSet).forEach(filter => {
      if (filtersToSet[filter] && filtersToSet[filter].includes(',')) {
        filtersToSet[filter].split(',').forEach((filterValue: any) => {
          setFilter({ filter, value: filterValue })
        })
      } else {
        setFilter({ filter, value: filtersToSet[filter] })
      }
    })
  }

  const resetFilter = (filter: string) => {
    forceUpdate.value = Date.now()
    if (Array.isArray(active.value[filter])) {
      active.value[filter] = []
      return
    }

    active.value[filter] = false
  }

  const reset = () => {
    active.value.sizes = []
    active.value.colors = []
    active.value.groups = []
    active.value.prices = []
    active.value.sort = 'fresh'
    active.value.available = false
  }

  const queryString = computed(() => {
    const queryParams = Object.assign({}, active.value)

    Object.keys(queryParams).map(key => {
      if (!active.value[key] || active.value[key].length === 0) {
        delete queryParams[key]
        return false
      }

      // sort_by=fresh is the default value. So can be removed from querystring
      if (key === 'sort' && active.value[key] === 'fresh') {
        delete queryParams[key]
      }

      // Turn arrays into comma seperated strings
      if (
        typeof active.value[key] === 'object' &&
        active.value[key] &&
        active.value[key].length > 0 &&
        queryParams[key]
      ) {
        queryParams[key] = queryParams[key].join(',')
      }
    })

    return queryParams
  })

  return {
    load,
    update,
    toggle,
    setFromQuery,
    reset,
    resetFilter,
    loading,
    totalProducts,
    available,
    active,
    forceUpdate,
    queryString
  }
})
